import { useEffect, useState } from "react";
import { useSession } from "@/auth/session";
import { createWarehouse, listWarehouses, type WarehouseItem } from "@/lib/api";

type FormState = {
  code: string;
  name: string;
  warehouseType: string;
  isCentral: boolean;
  active: boolean;
};

const emptyForm: FormState = {
  code: "",
  name: "",
  warehouseType: "general",
  isCentral: false,
  active: true,
};

export default function BodegasTab() {
  const { session } = useSession();
  const [items, setItems] = useState<WarehouseItem[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError(null);
      try {
        const rows = await listWarehouses(session);
        if (!cancelled) setItems(rows);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudieron cargar bodegas");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  function resetForm(): void {
    setForm(emptyForm);
  }

  function openCreateModal(): void {
    resetForm();
    setShowCreateModal(true);
  }

  function closeCreateModal(): void {
    setShowCreateModal(false);
    resetForm();
  }

  async function onSave(): Promise<void> {
    if (!session) return;
    if (!form.code.trim() || !form.name.trim() || !form.warehouseType.trim()) {
      setError("Completa código, nombre y tipo de bodega.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const created = await createWarehouse(session, {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        warehouseType: form.warehouseType.trim().toLowerCase(),
        isCentral: form.isCentral,
        active: form.active,
      });
      setItems((prev) => [created, ...prev]);
      closeCreateModal();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo crear bodega");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">Bodegas ({items.length})</h2>
          <button onClick={openCreateModal} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <i className="ri-add-line" />
            Nueva bodega
          </button>
        </div>

        {loading ? <div className="h-16 bg-gray-100 rounded animate-pulse" /> : (
          <div className="space-y-2">
            {items.length === 0 ? <p className="text-sm text-gray-500">Sin bodegas registradas.</p> : items.map((item) => (
              <div key={item.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                <div>
                  <div className="text-sm font-medium text-gray-800">{item.name}</div>
                  <div className="text-xs text-gray-500 font-mono">{item.code}</div>
                  <div className="text-xs text-gray-400">Tipo: {item.warehouseType}</div>
                </div>
                <div className="flex items-center gap-2">
                  {item.isCentral ? <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">Central</span> : null}
                  <span className={`text-xs px-2 py-1 rounded-full ${item.active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                    {item.active ? "Activa" : "Inactiva"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-gray-100 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Nueva bodega</h3>
              <button onClick={closeCreateModal} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400"><i className="ri-close-line" /></button>
            </div>
            <div className="space-y-3">
              <input
                value={form.code}
                onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                placeholder="Código (ej: BOD-CENTRAL)"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              />
              <input
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Nombre bodega"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              />
              <input
                value={form.warehouseType}
                onChange={(e) => setForm((prev) => ({ ...prev, warehouseType: e.target.value }))}
                placeholder="Tipo (ej: central, terreno, obra)"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.isCentral}
                  onChange={(e) => setForm((prev) => ({ ...prev, isCentral: e.target.checked }))}
                />
                Bodega central
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
                />
                Bodega activa
              </label>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={closeCreateModal} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg">Cancelar</button>
              <button
                onClick={() => void onSave()}
                disabled={saving || !form.code.trim() || !form.name.trim() || !form.warehouseType.trim()}
                className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-60"
              >
                {saving ? "Creando..." : "Crear bodega"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
